import React, { useContext, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { Box, CssBaseline, Typography, Drawer, AppBar, Toolbar, IconButton, useMediaQuery } from '@mui/material';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';

import Loading from './Loading';
import { getRuta } from '../helpers/Helpers';
import { DataContext } from '../context/DataContext';
import NavBarElements from './NavBarElements';
import { Route, Routes, useLocation } from 'react-router-dom';
import AccountContainer from '../pages/account/AccountContainer';
import NavLinks from './links/NavLinks';
import SidebarFooter from './SidebarFooter';
import HomeContainer from '../pages/home/HomeContainer';
import './estilos/estilos.css';
import { isEmpty } from 'lodash';

import MainLocations from '../pages/settings/locations/MainLocations';
import Municipalities from '../pages/settings/locations/Municipalities';
import { Communities } from '../pages/settings/locations/Communities';
import Unautorized from './Unautorized';
import MainInteresCorriente from '../pages/settings/interesCorriente/MainInteresCorriente';
import PeriodicidadList from '../pages/settings/Periodicidad/PeriodicidadList';
import TasaCambioList from '../pages/settings/tasaCambio/TasaCambioList';
import ClientList from '../pages/clients/ClientList';
import AddClient from '../pages/clients/AddClient';
import { DetalleClientContainer } from '../pages/clients/detalleCliente/DetalleClientContainer';
import CreditLIst from '../pages/credits/CreditLIst';
import AddCredit from '../pages/credits/AddCredit';
import CreditDetailsContainer from '../pages/credits/creditDetails/CreditDetailsContainer';
import Userlist from '../pages/security/users/Userlist';
import RolList from '../pages/security/rols/RolList';
import AddUser from '../pages/security/users/AddUser';
import EditUser from '../pages/security/users/EditUser';
import AddRol from '../pages/security/rols/AddRol';
import EditRol from '../pages/security/rols/EditRol';
import HolidayList from '../pages/settings/holidays/HolidayList';
import Reports from '../pages/reports/Reports';

import ClientesGestor from '../pages/reports/reportesCartera/ClientesGestor';
import CreditsByGestor from '../pages/reports/reportesCartera/CreditsByGestor';
import CobroDiario from '../pages/reports/reportesCartera/CobroDiario';
import Recuperacion from '../pages/reports/reportesCartera/Recuperacion';
import Desembolsos from '../pages/reports/reportesCartera/Desembolsos';
import SaldosCartera from '../pages/reports/reportesCartera/SaldosCartera';
import CreditsByBarrio from '../pages/reports/reportesCartera/CreditsByBarrio';
import ProyeccionIntereses from '../pages/reports/reportesCartera/ProyeccionIntereses';
import RecuperacionEfectiva from '../pages/reports/reportesCartera/RecuperacionEfectiva';
import { getUser } from '../api/Account';
import DefeatedCredits from '../pages/reports/reportesCartera/DefeatedCredits';
import AccountStatus from '../pages/reports/reportesCartera/AccountStatus';
import Caja from '../pages/admon/caja/Caja';
import Gastos from '../pages/admon/Gastos';
import CierreCaja from '../pages/reports/reportesCartera/CierreCaja';
import CierreCajaDetallado from '../pages/reports/reportesCartera/CierreCajaDetallado';
import EstadoResultado from '../pages/reports/reportesCartera/EstadoResultado';
import ConceptoGastoList from '../pages/settings/gastos/ConceptoGastoList';
import ServerRollist from '../pages/security/server/ServerRollist';
import CreditsCancelled from '../pages/reports/reportesCartera/CreditsCancelled';
import VencimientosProyectados from '../pages/reports/reportesCartera/VencimientosProyectados';
import SucursalList from '../pages/settings/sucursals/SucursalList';
import RutaGestor from '../pages/reports/reportesCartera/RutaGestor';
import GestoresEnVivo from '../pages/reports/reportesCartera/GestoresEnVivo';
import RutaCobro from '../pages/reports/reportesCartera/RutaCobro';
import ComportamientoPago from '../pages/reports/reportesCartera/ComportamientoPago';
import CumplimientoVisitas from '../pages/reports/reportesCartera/CumplimientoVisitas';
import MoraPorZona from '../pages/reports/reportesCartera/MoraPorZona';
import RecuperacionByCLient from '../pages/reports/reportesCartera/RecuperacionByCLient';
import { getAppTheme, sidebar } from '../theme';

const SIDEBAR_WIDTH = 250;

const SidebarContent = ({ docTitle, version, icon, setIsLogged, setIsDarkMode }) => (
	<Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: sidebar.bg, padding: '22px 16px', gap: '6px' }}>
		<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '6px 8px 22px' }}>
			<Box
				sx={{
					width: 40,
					height: 40,
					borderRadius: '11px',
					backgroundColor: '#fff',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					flexShrink: 0
				}}
			>
				<img loading="lazy" src={icon} alt="logo" style={{ width: 30, height: 30, objectFit: 'contain' }} />
			</Box>
			<Box sx={{ lineHeight: 1 }}>
				<Typography sx={{ color: '#fff', fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: 17 }}>
					{docTitle}
				</Typography>
				<Typography sx={{ fontSize: 11, color: sidebar.mutedColor, marginTop: '3px' }}>{version}</Typography>
			</Box>
		</Box>

		<Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
			<NavLinks drawerIsOpen />
		</Box>

		<SidebarFooter setIsLogged={setIsLogged} setIsDarkMode={setIsDarkMode} />
	</Box>
);

export default function MiniDrawer() {
	const { isDarkMode, setIsDarkMode, access, docTitle, version, icon, setIsLogged } = useContext(DataContext);
	const isMobile = useMediaQuery('(max-width:599.95px)');
	const [mobileOpen, setMobileOpen] = React.useState(false);
	const location = useLocation();

	let rout = getRuta();
	const user = getUser();

	useEffect(() => {
		if (isMobile) {
			setMobileOpen(false);
		}
	}, [location.pathname, isMobile]);

	const tema = getAppTheme(isDarkMode);

	if (isEmpty(access)) {
		return <Loading />;
	}

	if (user === null) {
		setIsLogged(false);
		return null;
	}

	return (
		<>
			<ThemeProvider theme={tema}>
				<Box sx={{ display: 'flex', height: '100vh', width: '100%', overflow: 'hidden', backgroundColor: 'background.default' }}>
					<CssBaseline />

					{isMobile ? (
						<Drawer
							variant="temporary"
							open={mobileOpen}
							onClose={() => setMobileOpen(false)}
							ModalProps={{ keepMounted: true }}
							PaperProps={{ sx: { width: SIDEBAR_WIDTH, border: 'none' } }}
						>
							<SidebarContent docTitle={docTitle} version={version} icon={icon} setIsLogged={setIsLogged} setIsDarkMode={setIsDarkMode} />
						</Drawer>
					) : (
						<Box component="aside" sx={{ width: SIDEBAR_WIDTH, flexShrink: 0 }}>
							<SidebarContent docTitle={docTitle} version={version} icon={icon} setIsLogged={setIsLogged} setIsDarkMode={setIsDarkMode} />
						</Box>
					)}

					<Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, height: '100%' }}>
						<AppBar
							position="static"
							elevation={0}
							sx={{
								height: 72,
								flexShrink: 0,
								backgroundColor: 'background.paper',
								borderBottom: '1px solid',
								borderColor: 'divider',
								color: 'text.primary'
							}}
						>
							<Toolbar sx={{ height: '100%', minHeight: '72px !important', paddingX: '28px' }}>
								{isMobile ? (
									<IconButton onClick={() => setMobileOpen(true)} edge="start" sx={{ marginRight: '12px' }}>
										<MenuRoundedIcon />
									</IconButton>
								) : null}
								<NavBarElements />
							</Toolbar>
						</AppBar>

						<Box component="main" sx={{ flex: 1, overflowY: 'auto', padding: { xs: '16px', sm: '26px 28px 40px' } }}>
							<Box>
								<Routes>
									<Route path={`${rout}/`} element={<HomeContainer />} />
									{/* Rulas Clientes */}
									<Route path={`${rout}/clients`} element={<ClientList />} />
									<Route path={`${rout}/addclient`} element={<AddClient />} />
									<Route path={`${rout}/client/:id`} element={<DetalleClientContainer />} />
									{/* Rulas Creditos */}
									<Route path={`${rout}/credits`} element={<CreditLIst />} />
									<Route path={`${rout}/credit/add/:id`} element={<AddCredit />} />
									<Route path={`${rout}/credit/:id`} element={<CreditDetailsContainer />} />
									{/* Rulas Admon */}
									<Route path={`${rout}/caja`} element={<Caja />} />
									<Route path={`${rout}/gastos`} element={<Gastos />} />
									{/* Rulas Reportes */}
									<Route path={`${rout}/reports`} element={<Reports />} />
									<Route
										path={`${rout}/r-clientsgestor/:selectedSucursal/:selectedStatus`}
										element={<ClientesGestor />}
									/>
									<Route
										path={`${rout}/r-creditsbyGestor/:selectedSucursal/:selectedGestor/:selectedLocalidad`}
										element={<CreditsByGestor />}
									/>
									<Route
										path={`${rout}/r-cobrodiario/:selectedSucursal/:selectedGestor/:selectedFecha/:selectedLocalidad`}
										element={<CobroDiario />}
									/>
									<Route
										path={`${rout}/r-recuperacion/:selectedSucursal/:selectedGestor/:fechaDesde/:fechaHasta`}
										element={<Recuperacion />}
									/>
									<Route
										path={`${rout}/r-recuperacionbyclient/:selectedSucursal/:selectedGestor/:fechaDesde/:fechaHasta`}
										element={<RecuperacionByCLient />}
									/>
									<Route
										path={`${rout}/r-desembolsos/:selectedSucursal/:selectedGestor/:fechaDesde/:fechaHasta`}
										element={<Desembolsos />}
									/>
									<Route path={`${rout}/r-saldoscartera/:selectedSucursal/:selectedGestor`} element={<SaldosCartera />} />
									<Route
										path={`${rout}/r-creditsbybarrio/:selectedSucursal/:selectedGestor/:selectedBarrio`}
										element={<CreditsByBarrio />}
									/>
									<Route
										path={`${rout}/r-proyeccionintereses/:selectedSucursal/:selectedGestor/:fechaHasta`}
										element={<ProyeccionIntereses />}
									/>
									<Route
										path={`${rout}/r-recuperacionefectiva/:selectedSucursal/:selectedGestor/:fechaDesde/:fechaHasta`}
										element={<RecuperacionEfectiva />}
									/>
									<Route
										path={`${rout}/r-defeatedcredits/:selectedSucursal/:selectedGestor`}
										element={<DefeatedCredits />}
									/>
									<Route
										path={`${rout}/r-proyecteddefeatedcredits/:selectedSucursal/:selectedGestor/:fechaDesde/:fechaHasta`}
										element={<VencimientosProyectados />}
									/>
									<Route path={`${rout}/r-accountstatus/:selectedCredit`} element={<AccountStatus />} />
									<Route path={`${rout}/r-cierrecaja/:selectedSucursal/:fecha`} element={<CierreCaja />} />
									<Route path={`${rout}/r-cierrecajad/:selectedSucursal/:fecha`} element={<CierreCajaDetallado />} />
									<Route
										path={`${rout}/r-estadoresultado/:selectedSucursal/:fechaDesde/:fechaHasta`}
										element={<EstadoResultado />}
									/>
									<Route
										path={`${rout}/r-creditscancelled/:selectedSucursal/:selectedGestor/:fechaDesde/:fechaHasta/:justNoReNew`}
										element={<CreditsCancelled />}
									/>
									<Route
										path={`${rout}/r-rutagestor/:selectedGestor/:selectedFecha`}
										element={<RutaGestor />}
									/>
									<Route
										path={`${rout}/r-gestoresenvivo`}
										element={<GestoresEnVivo />}
									/>
									<Route
										path={`${rout}/r-rutacobro/:selectedGestor/:selectedFecha`}
										element={<RutaCobro />}
									/>
									<Route
										path={`${rout}/r-comportamientopago/:selectedSucursal/:selectedGestor/:soloActivos`}
										element={<ComportamientoPago />}
									/>
									<Route
										path={`${rout}/r-cumplimientovisitas/:selectedGestor/:selectedFecha`}
										element={<CumplimientoVisitas />}
									/>
									<Route
										path={`${rout}/r-moraporzona/:selectedSucursal/:selectedGestor`}
										element={<MoraPorZona />}
									/>
									{/* Rulas Security-users */}
									<Route path={`${rout}/users`} element={<Userlist />} />
									<Route path={`${rout}/adduser`} element={<AddUser />} />
									<Route path={`${rout}/user/:id`} element={<EditUser />} />
									{/* Rulas Security-rols */}
									<Route path={`${rout}/rols`} element={<RolList />} />
									<Route path={`${rout}/addrol`} element={<AddRol />} />
									<Route path={`${rout}/rol/:id`} element={<EditRol />} />
									{/* Rulas Security-server */}
									<Route path={`${rout}/server`} element={<ServerRollist />} />
									<Route path={`${rout}/account`} element={<AccountContainer />} />
									{/* Rulas Ubicaciones */}
									<Route path={`${rout}/sucursales`} element={<SucursalList />} />
									<Route path={`${rout}/locations`} element={<MainLocations />} />
									<Route path={`${rout}/department/:id`} element={<Municipalities />} />
									<Route path={`${rout}/locations/municipalities/:id`} element={<Communities />} />
									<Route path={`${rout}/tasa-interes`} element={<MainInteresCorriente />} />
									<Route path={`${rout}/periodicidad`} element={<PeriodicidadList />} />
									<Route path={`${rout}/holidays`} element={<HolidayList />} />
									<Route path={`${rout}/conceptogastos`} element={<ConceptoGastoList />} />
									<Route path={`${rout}/tasa-cambio`} element={<TasaCambioList />} />
								</Routes>
							</Box>
						</Box>
					</Box>
				</Box>
				<Loading />
			</ThemeProvider>
			<Unautorized />
		</>
	);
}
